import { useCallback, useEffect, useRef, useState } from 'react';

import { getAdminUserDetail, getAdminUsers, blockAdminUser, unblockAdminUser, verifyAdminUserEmail, deleteAdminUser, type AdminUserDetail, type AdminUserRow, type PaginationMeta } from '../api/admin';

export function useAdminUsers(query?: string, page = 1, pageSize = 10, blocked?: boolean) {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({ total: 0, page: 1, pageSize, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const refreshingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setLoading(true);
    try {
      const response = await getAdminUsers({ q: query, page, pageSize, blocked });
      const payload = response.data.data as any;
      const items = Array.isArray(payload) ? payload : (payload?.items || []);
      const paginationMeta = payload?.pagination || {
        total: items.length,
        page,
        pageSize,
        totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
      };
      setUsers(items);
      setPagination(paginationMeta);
      return items;
    } finally {
      setLoading(false);
      refreshingRef.current = false;
    }
  }, [page, pageSize, query, blocked]);

  useEffect(() => {
    refresh().catch(() => {
      setUsers([]);
      setPagination({ total: 0, page: 1, pageSize, totalPages: 1 });
    });
  }, [refresh]);

  const loadUserDetail = useCallback(async (userId: number) => {
    setLoadingDetail(true);
    try {
      const response = await getAdminUserDetail(userId);
      setSelectedUser(response.data.data);
      return response.data.data;
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const blockUserAction = useCallback(async (userId: number, reason?: string) => {
    await blockAdminUser(userId, reason);
    refresh().catch(() => undefined);
    if (selectedUser?.user.id === userId) loadUserDetail(userId).catch(() => undefined);
  }, [refresh, loadUserDetail, selectedUser]);

  const unblockUserAction = useCallback(async (userId: number) => {
    await unblockAdminUser(userId);
    refresh().catch(() => undefined);
    if (selectedUser?.user.id === userId) loadUserDetail(userId).catch(() => undefined);
  }, [refresh, loadUserDetail, selectedUser]);

  const verifyUserEmailAction = useCallback(async (userId: number) => {
    await verifyAdminUserEmail(userId);
    refresh().catch(() => undefined);
    if (selectedUser?.user.id === userId) loadUserDetail(userId).catch(() => undefined);
  }, [refresh, loadUserDetail, selectedUser]);

  const deleteUserAction = useCallback(async (userId: number, reason?: string) => {
    await deleteAdminUser(userId, reason);
    if (selectedUser?.user.id === userId) setSelectedUser(null);
    refresh().catch(() => undefined);
  }, [refresh, selectedUser]);

  return {
    users, pagination, loading, refresh,
    selectedUser, loadingDetail, loadUserDetail,
    clearSelectedUser: () => setSelectedUser(null),
    blockUser: blockUserAction,
    unblockUser: unblockUserAction,
    verifyUserEmail: verifyUserEmailAction,
    deleteUser: deleteUserAction,
  };
}
